import CreateCampaignPreview from "../../CreateCampaign/CampaignPreview";
import {
  Button,
  HStack,
  Box,
  Spinner,
  Stack,
  Text,
  useMediaQuery,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import actions from "src/actions";
import { VersionedTemplates } from "common/email-templates";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import {
  KYDBlockData,
  KYDInternalCampaign,
  KYDRenderedCampaignResponse,
} from "@common/interfaces/KYDCampaign";
import Metrics from "./Metrics";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowBackIcon, ChevronDownIcon } from "@chakra-ui/icons";
import KYDMenuButton from "src/ui/KYDMenuButton";
import { formatAddress } from "common/services/util/address";
import { KYDScope } from "@common/interfaces/ICognito";
import { useDialog } from "src/Common/Dialog";

export interface FormValues {
  title: string;
  additional_audiences?: string[];
  subject: string;
  subject_preview: string;
  preheader?: string;
  send_at?: string;
  blocks: KYDBlockData[];
}

const CampaignMetrics = ({
  permissions = [],
}: {
  permissions?: KYDScope[];
}) => {
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  const [isIntents, setIntentsView] = useState<boolean>(true);
  const [organization, setOrganization] = useState<KYDOrganizationFull>();
  const [emailHtml, setEmailHtml] = useState<string>("");
  const [campaign, setCampaign] = useState<KYDInternalCampaign>();
  const isMobile = useMediaQuery("(max-width: 768px)")[0];
  const toast = useToast();
  const { id } = useParams();
  const [loading, setLoading] = useState({
    organizationUsers: true,
    sendingTestEmail: false,
    organization: true,
    campaign: true,
  });
  const [isDuplicating, setIsDuplicating] = useState(false);
  const navigate = useNavigate();
  const dialog = useDialog();

  const duplicateCampaign = useCallback(async () => {
    try {
      setIsDuplicating(true);
      const { id: newCampaignId } = await actions.ADMIN.duplicateCampaign(id);
      toast({
        title: "Campaign duplicated",
        description:
          "Campaign duplicated successfully. You're now editing the new campaign.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      console.log("[duplicateCampaign] newCampaignId", newCampaignId);
      navigate(`/campaigns/create?id=${newCampaignId}`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDuplicating(false);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      setIsDuplicating(true);
      await actions.ADMIN.deleteCampaign(campaign.id);
      toast({
        title: "Campaign deleted",
        description: "Campaign deleted successfully",
        status: "success",
      });
      navigate(`/campaigns`);
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast({
        title: "Error deleting campaign",
        description: error.message,
        status: "error",
      });
    }
    setIsDuplicating(false);
  }, [campaign]);

  const fetchOrganization = useCallback(async () => {
    setLoading((prev) => ({ ...prev, organization: true }));
    try {
      const organization = await actions.ADMIN.fetchOrganization();
      setOrganization(organization);
    } catch (err) {
      console.error(err);
    }
    setLoading((prev) => ({ ...prev, organization: false }));
  }, []);

  const fetchCampaign = useCallback(async () => {
    setLoading((prev) => ({ ...prev, campaign: true }));
    try {
      const { campaign, html_string }: KYDRenderedCampaignResponse =
        await actions.ADMIN.fetchMarketingCampaign(id);
      setCampaign(campaign);
      setEmailHtml(html_string);
    } catch (err) {
      console.error(err);
      dialog({
        text: err.message,
        actions: [
          {
            type: "confirm",
            text: "Got it",
            onClick: () => {
              window.location.assign("/");
            },
          },
        ],
      });
    }
    setLoading((prev) => ({ ...prev, campaign: false }));
  }, [id]);

  const onCancelCampaign = useCallback(async () => {
    try {
      await actions.ADMIN.cancelCampaign(campaign?.id);
      if (campaign?.status === "ACTIVE") {
        toast({
          title: "Campaign cancelled",
          description: "Campaign cancelled successfully",
          status: "success",
        });
        return;
      }

      if (campaign?.status === "OPEN") {
        navigate(`/campaigns/create?id=${campaign?.id}`);
        return;
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error cancelling campaign",
        description: err.message,
        status: "error",
      });
    }
  }, [campaign]);

  useEffect(() => {
    fetchOrganization();
    fetchCampaign();
  }, [fetchOrganization, fetchCampaign]);

  if (loading.organization || loading.campaign) {
    return (
      <VStack
        h="100%"
        w="100%"
        justifyContent="center"
        alignItems="center"
        py={4}
      >
        <Spinner />
      </VStack>
    );
  }
  if (!campaign) return null;
  return (
    <VStack h={"100%"} gap={0}>
      <HStack
        w="100%"
        justifyContent="space-between"
        position={"sticky"}
        px={{ base: 3, md: 5 }}
        pt={0}
        zIndex={1}
      >
        <VStack
          alignItems="flex-start"
          justifyContent={"space-between"}
          py={4}
          spacing={2}
        >
          <Link to="/campaigns" style={{ textDecoration: "none" }}>
            <Button
              variant="link"
              color="#888888"
              fontWeight="500"
              fontSize="14px"
              leftIcon={<ArrowBackIcon />}
              height="auto"
              minW="auto"
            >
              All Campaigns
            </Button>
          </Link>
          <Text
            fontSize="24px"
            color="#1A202C"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight="30px"
            letterSpacing="-0.72px"
          >
            {campaign.name}
          </Text>
        </VStack>
        <KYDMenuButton
          isLoading={isDuplicating}
          title="Actions"
          options={
            campaign.status === "DRAFT"
              ? [
                  {
                    label: "Duplicate Email Campaign",
                    onClick: duplicateCampaign,
                  },
                  { label: "Delete Draft", onClick: handleDelete },
                ]
              : [
                  {
                    label: "Duplicate Email Campaign",
                    onClick: duplicateCampaign,
                  },
                ]
          }
        />
      </HStack>
      {isMobile && (
        <HStack w="100%" px={6} pb={4}>
          <Box margin="auto" bg="gray.200" borderRadius="full" p={1}>
            <HStack spacing={0}>
              <Button
                size="sm"
                borderRadius="full"
                px={4}
                py={2}
                bg={isIntents ? "white" : "transparent"}
                color={isIntents ? "black" : "gray.600"}
                boxShadow={isIntents ? "sm" : "none"}
                onClick={() => setIntentsView(true)}
                _hover={{ bg: isIntents ? "white" : "gray.100" }}
              >
                Intents
              </Button>
              <Button
                size="sm"
                borderRadius="full"
                px={4}
                py={2}
                bg={!isIntents ? "white" : "transparent"}
                color={!isIntents ? "black" : "gray.600"}
                boxShadow={!isIntents ? "sm" : "none"}
                onClick={() => setIntentsView(false)}
                _hover={{ bg: !isIntents ? "white" : "gray.100" }}
              >
                Email
              </Button>
            </HStack>
          </Box>
        </HStack>
      )}
      <Stack
        w="100%"
        height={"100%"}
        direction={{ base: "column", lg: "row" }}
        gap={0}
        p={0}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        {(!isMobile || isIntents) && (
          <Box
            w={{ base: "100%" }}
            minW={{ base: "100%", lg: "375px", xl: "475px" }}
            maxW={{ base: "100%", lg: "475px" }}
          >
            <Metrics
              permissions={permissions}
              isMobile={isMobile}
              campaign={campaign}
              onCancelCampaign={onCancelCampaign}
            />
          </Box>
        )}
        {(!isMobile || !isIntents) && (
          <CreateCampaignPreview
            showPreview={true}
            emailHtml={emailHtml || ""}
            view={view}
            onFinishRendering={() =>
              setLoading((prev) => ({ ...prev, renderingEmail: false }))
            }
            setView={setView}
            isMobile={isMobile}
          />
        )}
      </Stack>
    </VStack>
  );
};

export default CampaignMetrics;
